import { useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ModalShell from "./ModalShell";
import Spinner from "../../components/Spinner/Spinner";
import {
  downloadStaffImportTemplate,
  importStaff,
} from "../../services/userService";
import { formatApiError } from "../../utils/helpers/apiErrors";

// The .xlsx file is parsed on the backend (accounts/staff_import.py), and the
// template comes from the same endpoint — this component never reads the
// spreadsheet itself, so the header can't drift between the two sides.
const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const MAX_XLSX_BYTES = 2 * 1024 * 1024; // 2 MB — a staff roster is tens of KB

const OUTCOME_CLASS = {
  create: "bg-emerald-50 text-emerald-600",
  skip: "bg-slate-100 text-slate-500",
  error: "bg-rose-50 text-rose-600",
};

// Same vocabulary and ordering as the timetable import report. `skip` replaces
// that report's `update` because this importer never modifies an existing
// account — see the module docstring on the backend.
const REPORT_FILTERS = [
  { key: "all", match: () => true },
  { key: "error", match: (row) => row.outcome === "error" },
  { key: "warning", match: (row) => (row.warnings || []).length > 0 },
  { key: "create", match: (row) => row.outcome === "create" },
  { key: "skip", match: (row) => row.outcome === "skip" },
];

// A row message arrives as {code, detail, params?}. Branch on the code and let
// the catalog own the wording — the same rule runErrors.js follows for the
// timetable endpoints. `detail` is the server's English fallback, shown only
// for a code this build has no key for yet.
const rowMessage = (message, t) => {
  const key = `users.import.err.${message.code}`;
  const text = t(key, message.params || {});
  return text === key ? message.detail || "" : text;
};

const CHIP_BASE =
  "rounded-full px-3 py-1 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500";

export default function StaffImportModal({ onClose, onImported }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("all");

  const reset = () => {
    setFile(null);
    setFileName("");
    setReport(null);
    setFilter("all");
    if (fileRef.current) fileRef.current.value = "";
  };

  const onFile = async (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    // Cleared immediately so re-picking the same filename (the fix-and-reupload
    // loop) fires a change event instead of silently doing nothing.
    e.target.value = "";
    if (picked.size > MAX_XLSX_BYTES) {
      toast.error(t("users.import.fileTooLarge"));
      return;
    }
    setFile(picked);
    setFileName(picked.name);
    setReport(null);
    setBusy("preview");
    try {
      const result = await importStaff({ file: picked, commit: false });
      if (!result?.summary?.total) {
        toast.error(t("users.import.emptyFile"));
        return;
      }
      setReport(result);
      // Land on the rows that need a decision. A clean file has none, so this
      // is a no-op exactly when there is nothing to look at.
      setFilter(result.summary.errors > 0 ? "error" : "all");
    } catch (err) {
      toast.error(formatApiError(err, t("users.import.previewFailed")));
    } finally {
      setBusy("");
    }
  };

  const apply = async () => {
    setBusy("commit");
    try {
      const result = await importStaff({ file, commit: true });
      setReport(result);
      toast.success(
        t("users.import.committed", {
          created: result.summary.created,
          skipped: result.summary.skipped,
        }),
      );
      onImported?.();
    } catch (err) {
      toast.error(formatApiError(err, t("users.import.applyFailed")));
    } finally {
      setBusy("");
    }
  };

  const downloadTemplate = async () => {
    try {
      const blob = await downloadStaffImportTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "staff-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err, t("users.import.templateFailed")));
    }
  };

  const summary = report?.summary;
  const committed = Boolean(summary?.committed);
  const canApply = Boolean(
    summary && summary.created > 0 && summary.errors === 0 && !committed,
  );

  const buckets = useMemo(() => {
    const all = report?.rows || [];
    return REPORT_FILTERS.map((f) => ({ ...f, rows: all.filter(f.match) }));
  }, [report]);

  // The chips are hidden on a short file, so the filter must not apply there
  // either — otherwise a six-row file with one error opens showing that one row
  // and no control anywhere to explain where the other five went.
  const canFilter = (report?.rows || []).length > 8;
  const visibleRows = canFilter
    ? buckets.find((b) => b.key === filter)?.rows || []
    : report?.rows || [];

  return (
    <ModalShell
      title={t("users.import.title")}
      subtitle={t("users.import.subtitle")}
      onClose={onClose}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <label className="cursor-pointer rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2">
            {t("users.import.chooseFile")}
            <input
              ref={fileRef}
              type="file"
              accept={`.xlsx,${XLSX_MIME}`}
              onChange={onFile}
              disabled={busy !== ""}
              className="sr-only"
            />
          </label>
          <span className="text-xs font-semibold text-slate-500">
            {fileName || t("users.import.noFile")}
          </span>
          <button
            type="button"
            onClick={downloadTemplate}
            className="ml-auto rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            {t("users.import.downloadTemplate")}
          </button>
          {(file || report) && (
            <button
              type="button"
              onClick={reset}
              className="rounded-xl px-3 py-2 text-xs font-semibold text-slate-500 hover:text-slate-700"
            >
              {t("users.import.reset")}
            </button>
          )}
        </div>

        {/* Said here rather than only in a success toast: an operator deciding
            whether to press Apply needs to know that nobody is emailed yet. */}
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-800">
          {t("users.import.noEmailNotice")}
        </p>

        {busy === "preview" && (
          <div className="flex justify-center py-8">
            <Spinner label={t("users.import.checking")} />
          </div>
        )}

        {summary && busy !== "preview" && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <SummaryStat label={t("users.import.statTotal")} value={summary.total} />
              <SummaryStat
                label={t("users.import.statCreate")}
                value={summary.created}
                tone="text-emerald-600"
              />
              <SummaryStat
                label={t("users.import.statSkip")}
                value={summary.skipped}
                tone="text-slate-500"
              />
              <SummaryStat
                label={t("users.import.statError")}
                value={summary.errors}
                tone="text-rose-600"
              />
            </div>

            {canFilter && (
              <div className="flex flex-wrap gap-2">
                {buckets.map((bucket) => (
                  <button
                    key={bucket.key}
                    type="button"
                    onClick={() => setFilter(bucket.key)}
                    className={`${CHIP_BASE} ${
                      filter === bucket.key
                        ? "bg-slate-900 text-white"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {t(`users.import.filter.${bucket.key}`)} ({bucket.rows.length})
                  </button>
                ))}
              </div>
            )}

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("users.import.colLine")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("users.import.colName")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("users.import.colEmail")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("users.import.colOutcome")}
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      {t("users.import.colDetail")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visibleRows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-xs text-slate-400">
                        {t("users.import.noMatchingRows")}
                      </td>
                    </tr>
                  )}
                  {visibleRows.map((row) => (
                    <tr key={row.line}>
                      <td className="px-3 py-2 text-xs tabular-nums text-slate-400">
                        {row.line}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {[row.first_name, row.last_name].filter(Boolean).join(" ") || "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{row.email || "—"}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                            OUTCOME_CLASS[row.outcome] || OUTCOME_CLASS.skip
                          }`}
                        >
                          {t(`users.import.outcome.${row.outcome}`)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {(row.errors || []).map((message) => (
                          <p key={message.code} className="text-rose-600">
                            {rowMessage(message, t)}
                          </p>
                        ))}
                        {(row.warnings || []).map((message) => (
                          <p key={message.code} className="text-amber-600">
                            {rowMessage(message, t)}
                          </p>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800"
          >
            {committed ? t("common.close") : t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!canApply || busy === "commit"}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy === "commit"
              ? t("users.import.applying")
              : t("users.import.apply", { count: summary?.created || 0 })}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function SummaryStat({ label, value, tone = "text-slate-700" }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </p>
      <p className={`text-lg font-bold tabular-nums ${tone}`}>{value}</p>
    </div>
  );
}
